import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Dialog } from "antd-mobile";
import { commentOrder, getDefaultForm, getOrderDetail } from "@/api/staff";
import type { StaffOrderDetail } from "@/api/staff";
import { toastSuccess, toastWarning } from "@/utils/toast";

type CommentFieldType = "radio" | "checkbox" | "textarea" | "input";
type CustomerUserInfo = { nickname?: string; avatar?: string } | null;

interface CommentConfigChild {
  title: string;
  value?: boolean;
}

interface CommentConfigItem {
  type: CommentFieldType;
  title: string;
  value?: string;
  children?: CommentConfigChild[];
}

const BRAND = "#FE7A3A";

export default function WorkspaceOrderCommentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const orderId = Number(searchParams.get("id") || 0);

  const [orderInfo, setOrderInfo] = useState<StaffOrderDetail | null>(null);
  const [list, setList] = useState<CommentConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const passedUserInfo = ((location.state as any)?.userInfo ?? null) as CustomerUserInfo;

  const userName = useMemo(
    () =>
      passedUserInfo?.nickname ||
      (orderInfo as any)?.userInfo?.nickname ||
      "",
    [orderInfo, passedUserInfo],
  );
  const userAvatar = useMemo(
    () => passedUserInfo?.avatar || (orderInfo as any)?.userInfo?.avatar || "",
    [orderInfo, passedUserInfo],
  );

  useEffect(() => {
    const load = async () => {
      if (!orderId) return;
      setLoading(true);
      try {
        const [detail, form] = await Promise.all([getOrderDetail(orderId), getDefaultForm()]);
        setOrderInfo(detail);
        setList(Array.isArray(form) ? (form as any) : ((form as any)?.list ?? (form as any)?.data ?? []));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId]);

  const updateItemValue = (idx: number, value: string) => {
    setList((prev) => prev.map((it, i) => (i === idx ? { ...it, value } : it)));
  };

  const toggleCheckbox = (idx: number, childIdx: number) => {
    setList((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const children = (it.children ?? []).map((c, k) => (k === childIdx ? { ...c, value: !c.value } : c));
        const selected = children.filter((c) => c.value).map((c) => c.title).join(",");
        return { ...it, children, value: selected };
      }),
    );
  };

  const validate = (): string | null => {
    if (!list.length) return "暂无可提交的表单项";
    for (const item of list) {
      const title = item.title || "该项";
      const v = String(item.value ?? "").trim();
      if (item.type === "radio" && !v) return `请选择「${title}」`;
      if (item.type === "checkbox") {
        const hasChecked = item.children?.some((c) => c.value) || v.length > 0;
        if (!hasChecked) return `请选择「${title}」`;
      }
      if (item.type === "textarea" && !v) return `请填写「${title}」`;
      if (item.type === "input" && !v) return `请填写「${title}」`;
    }
    return null;
  };

  const submit = async () => {
    if (!orderId) return toastWarning("缺少订单ID");
    if (submitting) return toastWarning("操作频繁，请稍后再试");
    const msg = validate();
    if (msg) return toastWarning(msg);

    Dialog.show({
      content: "确认提交评价？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "提交",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              setSubmitting(true);
              try {
                const fd = new FormData();
                fd.append("order_id", String(orderId));
                fd.append("content", JSON.stringify(list));
                await commentOrder(fd);
                toastSuccess("提交成功");
                setTimeout(() => navigate(-1), 800);
              } finally {
                setSubmitting(false);
              }
            },
          },
        ],
      ],
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <i className="ri-loader-4-line text-2xl animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f7]" style={{ fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      <div className="sticky top-0 z-20" style={{ background: BRAND }}>
        <div className="px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13 }}>
          <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}>
            <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#fff" }} />
          </button>
          <span className="text-lg font-bold text-white">订单评价</span>
        </div>
      </div>

      <div className="px-4 pt-4 pb-10 space-y-4">
        <div className="bg-white rounded-2xl">
          <div className="flex items-center p-4">
            {userAvatar ? (
              <img src={userAvatar} alt="" className="w-11 h-11 rounded-full object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-gray-200" />
            )}
            <div className="text-sm ml-3 font-bold">用户：{userName || "—"}</div>
          </div>

          {list.map((item, idx) => {
            if (item.type !== "radio") return null;
            return (
              <div key={idx} className="px-4 py-4 border-t border-gray-100">
                <div className="text-sm font-medium mb-2">{item.title}</div>
                <select
                  value={item.value || ""}
                  onChange={(e) => updateItemValue(idx, e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="">请选择</option>
                  {(item.children ?? []).map((c, k) => (
                    <option key={k} value={c.title}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl p-4 space-y-5">
          {list.map((item, idx) => {
            if (item.type === "checkbox") {
              return (
                <div key={idx}>
                  <div className="text-sm font-bold mb-3">{item.title}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(item.children ?? []).map((c, k) => (
                      <button
                        type="button"
                        key={k}
                        onClick={() => toggleCheckbox(idx, k)}
                        className={`rounded-xl py-2 text-xs font-medium border ${c.value ? "bg-[#FE7A3A] text-white border-[#FE7A3A]" : "bg-gray-100 text-gray-700 border-transparent"}`}
                      >
                        {c.title}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }

            if (item.type === "textarea") {
              return (
                <div key={idx}>
                  <div className="text-sm font-bold mb-3">{item.title}</div>
                  <textarea
                    value={item.value || ""}
                    onChange={(e) => updateItemValue(idx, e.target.value)}
                    placeholder="请输入对于用户的情况描述"
                    className="w-full rounded-2xl bg-gray-100 p-3 text-sm outline-none resize-none min-h-[120px]"
                  />
                </div>
              );
            }

            if (item.type === "input") {
              return (
                <div key={idx}>
                  <div className="text-sm font-bold mb-3">{item.title}</div>
                  <input
                    value={item.value || ""}
                    onChange={(e) => updateItemValue(idx, e.target.value)}
                    placeholder={`请输入${item.title}`}
                    className="w-full rounded-2xl bg-gray-100 px-3 py-3 text-sm outline-none"
                  />
                </div>
              );
            }

            return null;
          })}
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className={`w-full py-3 rounded-full text-white font-semibold active:scale-[0.98] ${submitting ? "opacity-60" : ""}`}
          style={{ background: BRAND }}
        >
          {submitting ? "提交中..." : "提交评价"}
        </button>
      </div>
    </div>
  );
}

